import { useAuth } from '@clerk/clerk-react';
import useSWR from 'swr';
import { toast } from 'sonner';

import { ApiError, spacesApi } from '@/lib/api';
import type { SpaceDetail, SpaceSummary } from '@/types/spaces';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

// Unlike general lookup data, membership must never carry across users or spaces.
const OPTIONS = {
  ...LONG_LIVED_SWR_OPTIONS,
  keepPreviousData: false,
  shouldRetryOnError: (error: unknown) => !(error instanceof ApiError && [403, 404].includes(error.status)),
};

function notifyListError() {
  toast.error('Não foi possível carregar seus espaços. Tente novamente.', { id: 'spaces-load-error' });
}

function notifySpaceError(error: Error) {
  if (error instanceof ApiError && [403, 404].includes(error.status)) return;
  toast.error('Não foi possível abrir o espaço. Tente novamente.', { id: 'space-load-error' });
}

export function useSpaces(enabled: boolean) {
  const { userId } = useAuth();
  const fetch = useAuthedFetcher();
  return useSWR<SpaceSummary[], Error>(
    enabled && userId ? ['spaces', userId] : null,
    () => fetch((token) => spacesApi.list(token)),
    { ...OPTIONS, onError: notifyListError },
  );
}

export function useSpace(spaceId: string | null, enabled: boolean) {
  const { userId } = useAuth();
  const fetch = useAuthedFetcher();
  return useSWR<SpaceDetail, Error>(
    enabled && userId && spaceId ? ['space', userId, spaceId] : null,
    () => fetch((token) => spacesApi.get(token, spaceId!)),
    { ...OPTIONS, onError: notifySpaceError },
  );
}
